/**
 * RegVyd API Client
 *
 * Centralized API communication with the Nette backend.
 * Auto-detects API URL based on current hostname:
 *   - regvyd.projekty.belion.cz → regvyd-backend.projekty.belion.cz
 *   - localhost:3000             → regvyd-backend.l (local dev)
 */

function resolveApiBase() {
  // Allow explicit override via env variable
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  const hostname = window.location.hostname;

  // Production / staging on belion.cz
  if (hostname === 'regvyd.projekty.belion.cz') {
    return 'https://regvyd-backend.projekty.belion.cz/api';
  }

  // Local development (default)
  return 'https://regvyd-backend.l/api';
}

const API_BASE = resolveApiBase();

class ApiClient {
  constructor() {
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken() {
    return this.token || localStorage.getItem('auth_token');
  }

  getBaseUrl() {
    return API_BASE;
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Handle 401 - redirect to login
      if (response.status === 401) {
        this.setToken(null);
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        throw new Error('Unauthorized');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`);
      }

      return data;
    } catch (error) {
      if (error.message === 'Unauthorized') throw error;
      console.error(`API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  get(endpoint, params = {}) {
    const queryString = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined)
      )
    ).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url, { method: 'GET' });
  }

  post(endpoint, body = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  put(endpoint, body = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // ==========================================
  // AUTH
  // ==========================================

  async login(email, password) {
    const result = await this.post('/auth/login', { email, password });
    if (result.data?.token) {
      this.setToken(result.data.token);
    }
    return result;
  }

  async logout() {
    try {
      await this.post('/auth/logout');
    } catch (e) {
      // Ignore errors on logout
    }
    this.setToken(null);
  }

  async checkAuth() {
    return this.get('/auth/check');
  }

  // ==========================================
  // CRM - Companies
  // ==========================================

  getCompanies(params = {}) {
    return this.get('/crm/companies', params);
  }

  getCompany(id) {
    return this.get(`/crm/companies/${id}`);
  }

  createCompany(data) {
    return this.post('/crm/companies/create', data);
  }

  updateCompany(id, data) {
    return this.put(`/crm/companies/${id}/update`, data);
  }

  deleteCompany(id) {
    return this.delete(`/crm/companies/${id}/delete`);
  }

  // Hledá už existující firmu podle IČ — formulář tím hlídá duplicity.
  // Vrací { company: {...} } nebo { company: null }.
  findCompanyByIco(ico) {
    return this.get(`/crm/companies/by-ico/${encodeURIComponent(ico)}`);
  }

  // Vyhledání firmy v ARESu podle IČ. Nic neukládá — jen vrátí data
  // pro předvyplnění formuláře.
  lookupAres(ico) {
    return this.get(`/crm/ares/${encodeURIComponent(ico)}`);
  }

  // CRM - Enums/Lookups
  getCrmEnums() {
    return this.get('/crm/enums');
  }

  // CRM - Sub-entities (list)
  getCompanyContacts(companyId) {
    return this.get(`/crm/companies/${companyId}/contacts`);
  }

  // Historie změn firmy — včetně adres, kontaktů, poznámek a událostí.
  getCompanyHistory(companyId, params = {}) {
    return this.get(`/crm/companies/${companyId}/history`, params);
  }

  getCompanyAddresses(companyId) {
    return this.get(`/crm/companies/${companyId}/addresses`);
  }

  getCompanyEvents(companyId) {
    return this.get(`/crm/companies/${companyId}/events`);
  }

  getCompanyCategories(companyId) {
    return this.get(`/crm/companies/${companyId}/categories`);
  }

  getCategories() {
    return this.get('/crm/categories');
  }

  // CRM - Address CRUD
  createAddress(data) {
    return this.post('/crm/addresses/create', data);
  }

  updateAddress(id, data) {
    return this.put(`/crm/addresses/${id}/update`, data);
  }

  deleteAddress(id) {
    return this.delete(`/crm/addresses/${id}/delete`);
  }

  // CRM - Contact CRUD
  createContact(data) {
    return this.post('/crm/contacts/create', data);
  }

  updateContact(id, data) {
    return this.put(`/crm/contacts/${id}/update`, data);
  }

  deleteContact(id) {
    return this.delete(`/crm/contacts/${id}/delete`);
  }

  // CRM - Contact notes (contact_person_note)
  getContactNotes(contactId) {
    return this.get(`/crm/contacts/${contactId}/notes`);
  }

  createContactNote(data) {
    return this.post('/crm/contact-notes/create', data);
  }

  updateContactNote(id, data) {
    return this.put(`/crm/contact-notes/${id}/update`, data);
  }

  deleteContactNote(id) {
    return this.delete(`/crm/contact-notes/${id}/delete`);
  }

  // CRM - Contact relationships (obchodníci / account manageři)
  // Hledá napříč celou tabulkou contact_person (34k+ lidí).
  searchContactPersons(query, limit = 30) {
    return this.get(`/crm/contact-persons/search?search=${encodeURIComponent(query || '')}&limit=${limit}`);
  }

  // Jen lidé z vlastní firmy — pro všechny výběry obchodníka. Jména se
  // v celé tabulce opakují, bez tohohle omezení jde snadno vybrat cizí osobu.
  searchSalespeople(query, limit = 30) {
    return this.get(`/crm/contact-persons/search?search=${encodeURIComponent(query || '')}&limit=${limit}&internal=1`);
  }

  // Přihlášený uživatel jako kontaktní osoba (obchodník), nebo null, když ho
  // nejde jednoznačně dohledat — účty a kontaktní osoby spolu nemají vazbu.
  getMyContactPerson() {
    return this.get('/crm/contact-persons/me');
  }

  getContactRelationships(contactId) {
    return this.get(`/crm/contacts/${contactId}/relationships`);
  }

  createContactRelationship(data) {
    return this.post('/crm/contact-relationships/create', data);
  }

  updateContactRelationship(id, data) {
    return this.put(`/crm/contact-relationships/${id}/update`, data);
  }

  deleteContactRelationship(id) {
    return this.delete(`/crm/contact-relationships/${id}/delete`);
  }

  // CRM - Company Bookings
  getCompanyBookings(companyId) {
    return this.get(`/crm/companies/${companyId}/bookings`);
  }

  // CRM - Event CRUD
  createEvent(data) {
    return this.post('/crm/events/create', data);
  }

  updateEvent(id, data) {
    return this.put(`/crm/events/${id}/update`, data);
  }

  deleteEvent(id) {
    return this.delete(`/crm/events/${id}/delete`);
  }

  // ==========================================
  // MEDIA
  // ==========================================

  getMedia(params = {}) {
    return this.get('/media', params);
  }

  getMedium(id) {
    return this.get(`/media/${id}`);
  }

  getMediaTypes() {
    return this.get('/media/types');
  }

  getMediumSections(mediumId) {
    return this.get(`/media/${mediumId}/sections`);
  }

  getMediumMutations(mediumId) {
    return this.get(`/media/${mediumId}/mutations`);
  }

  createMutation(mediumId, data) {
    return this.post(`/media/${mediumId}/mutations/create`, data);
  }

  updateMutation(mutationId, data) {
    return this.put(`/mutations/${mutationId}/update`, data);
  }

  deleteMutation(mutationId) {
    return this.delete(`/mutations/${mutationId}/delete`);
  }

  createSection(mediumId, data) {
    return this.post(`/media/${mediumId}/sections/create`, data);
  }

  updateSection(sectionId, data) {
    return this.put(`/sections/${sectionId}/update`, data);
  }

  deleteSection(sectionId) {
    return this.delete(`/sections/${sectionId}/delete`);
  }

  getMediumProjects(mediumId, params = {}) {
    return this.get(`/media/${mediumId}/projects`, params);
  }

  // jenAktivni = jen ceníky se stavem Aktivní (výběr v položce objednávky);
  // správa ceníků si bere celý seznam včetně historických ročníků
  getMediumRatecards(mediumId, jenAktivni = false) {
    return this.get(`/media/${mediumId}/ratecards${jenAktivni ? '?available=1' : ''}`);
  }

  createMedium(data) {
    return this.post('/media/create', data);
  }

  updateMedium(id, data) {
    return this.put(`/media/${id}/update`, data);
  }

  searchCompanies(query, limit = 15) {
    return this.get('/crm/companies', { search: query, limit, sort: 'company_name', order: 'asc' });
  }

  // ==========================================
  // PROJECTS
  // ==========================================

  getProject(id) {
    return this.get(`/projects/${id}`);
  }

  createProject(data) {
    return this.post('/projects/create', data);
  }

  updateProject(id, data) {
    return this.put(`/projects/${id}/update`, data);
  }

  deleteProject(id) {
    return this.delete(`/projects/${id}/delete`);
  }

  getProjectBookingItems(projectId) {
    return this.get(`/projects/${projectId}/booking-items`);
  }

  // ==========================================
  // RATECARDS
  // ==========================================

  getRatecard(id) {
    return this.get(`/ratecards/${id}`);
  }

  createRatecard(data) {
    return this.post('/ratecards/create', data);
  }

  updateRatecard(id, data) {
    return this.put(`/ratecards/${id}/update`, data);
  }

  getRatecardItems(ratecardId) {
    return this.get(`/ratecards/${ratecardId}/items`);
  }

  getRatecardFormats() {
    return this.get('/ratecards/formats');
  }

  getRatecardUnits() {
    return this.get('/ratecards/units');
  }

  // ==========================================
  // ORDERS
  // ==========================================

  getOrders(params = {}) {
    return this.get('/orders', params);
  }

  getOrder(id) {
    return this.get(`/orders/${id}`);
  }

  createOrder(data) {
    return this.post('/orders/create', data);
  }

  updateOrder(id, data) {
    return this.put(`/orders/${id}/update`, data);
  }

  getOrderEnums() {
    return this.get('/orders/enums');
  }

  // ==========================================
  // INVOICES
  // ==========================================

  getInvoiceEnums() {
    return this.get('/invoices/enums');
  }

  searchInvoices(q, limit = 15) {
    return this.get('/invoices/search', { q, limit });
  }

  getInvoices(params = {}) {
    return this.get('/invoices', params);
  }

  getInvoice(id) {
    return this.get(`/invoices/${id}`);
  }

  /** Zálohový cyklus — položky zálohy, nepotvrzené platby, stav doplacení. */
  getInvoiceAdvanceInfo(id) {
    return this.get(`/invoices/${id}/advance-info`);
  }

  /** Potvrzení platby k záloze (daňový doklad k přijaté platbě, řada 2613). */
  createPaymentConfirmation(id, invoiceItemIds = []) {
    return this.post(`/invoices/${id}/payment-confirmation`, { invoiceItemIds });
  }

  /** Vyúčtovací faktura k doplacené záloze. */
  createFinalInvoice(id) {
    return this.post(`/invoices/${id}/final-invoice`);
  }

  /** Komu by faktura šla e-mailem (adresát, předmět, fallback na obchodníka). */
  getInvoiceEmailInfo(id) {
    return this.get(`/invoices/${id}/email-info`);
  }

  /** Odeslání faktury e-mailem; `toSalesperson` pošle doklad obchodníkovi. */
  // email/name = ruční adresát místo toho, který plyne z faktury
  sendInvoiceEmail(id, { toSalesperson = false, email = null, name = null } = {}) {
    return this.post(`/invoices/${id}/send-email`, { toSalesperson, email, name });
  }

  /** Hromadné odeslání — vrací `{sent, failed}`. */
  sendInvoiceEmails(invoiceIds) {
    return this.post('/invoices/send-email', { invoiceIds });
  }

  /** Podklady pro dialog mazání — varování a navazující doklady zálohového řetězce. */
  getInvoiceDeleteInfo(id) {
    return this.get(`/invoices/${id}/delete-info`);
  }

  /** Softové smazání faktury (číslo se nerecykluje). */
  deleteInvoice(id, { reason = '', withRelated = false } = {}) {
    return this.request(`/invoices/${id}/delete`, {
      method: 'DELETE',
      body: JSON.stringify({ reason, withRelated }),
    });
  }

  restoreInvoice(id) {
    return this.post(`/invoices/${id}/restore`);
  }

  createInvoices(data) {
    return this.post('/invoices/create', data);
  }

  /** Faktura bez objednávky — založí se hlavička, položky se dopisují v detailu. */
  createManualInvoice(data) {
    return this.post('/invoices/create-manual', data);
  }

  /** Podklad pro dobropis: položky faktury, kolik z nich zbývá, a číselník důvodů. */
  getInvoiceCreditNoteInfo(id) {
    return this.get(`/invoices/${id}/credit-note-info`);
  }

  /** Vystaví dobropis k faktuře. Částky v `items` jsou bez DPH a kladné. */
  createInvoiceCreditNote(id, data) {
    return this.post(`/invoices/${id}/credit-note`, data);
  }

  /**
   * POST, jehož odpovědí je soubor (XML, PDF). Obchází `request()`,
   * ten by odpověď parsoval jako JSON. Název souboru bere z Content-Disposition.
   *
   * @returns {Promise<{blob: Blob, filename: string, stats: object, warnings: string[]}>}
   */
  async postForFile(path, body, { fallbackName, statsHeader, warningsHeader }) {
    const headers = { 'Content-Type': 'application/json' };
    const token = this.getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (response.status === 401) {
      this.setToken(null);
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      // Chybová odpověď je zase JSON, i když úspěšná je soubor.
      let message = `HTTP ${response.status}`;
      try {
        const data = await response.json();
        message = data.message || message;
      } catch (e) { /* odpověď nebyla JSON, zůstane HTTP kód */ }
      throw new Error(message);
    }

    const parseHeader = (name, fallback) => {
      try {
        return JSON.parse(response.headers.get(name) || '') ?? fallback;
      } catch (e) {
        return fallback;
      }
    };

    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/);

    return {
      blob: await response.blob(),
      filename: match ? match[1] : fallbackName,
      stats: parseHeader(statsHeader, {}),
      warnings: parseHeader(warningsHeader, []),
    };
  }

  /** XML feed pro Money S3 (windows-1250). */
  exportInvoicesToMoney(invoiceIds) {
    return this.postForFile('/invoices/export-money', { invoiceIds }, {
      fallbackName: 'money-faktury.xml',
      statsHeader: 'X-Money-Stats',
      warningsHeader: 'X-Money-Warnings',
    });
  }

  /** Vybrané faktury do Excelu — stejné sloupce jako výpis faktur. */
  exportInvoicesToXlsx(invoiceIds) {
    return this.postForFile('/invoices/export-xlsx', { invoiceIds }, {
      fallbackName: 'faktury.xlsx',
    });
  }

  /** PDF vybraných faktur — všechny slepené do jednoho souboru za sebou. */
  exportInvoicesToPdf(invoiceIds) {
    return this.postForFile('/invoices/export-pdf', { invoiceIds }, {
      fallbackName: 'faktury.pdf',
      statsHeader: 'X-Pdf-Stats',
      warningsHeader: 'X-Pdf-Warnings',
    });
  }

  updateInvoice(id, data) {
    return this.put(`/invoices/${id}/update`, data);
  }

  // Invoice Items
  createInvoiceItem(invoiceId, data) {
    return this.post(`/invoices/${invoiceId}/items/create`, data);
  }

  updateInvoiceItem(itemId, data) {
    return this.put(`/invoices/items/${itemId}/update`, data);
  }

  deleteInvoiceItem(itemId) {
    return this.delete(`/invoices/items/${itemId}/delete`);
  }

  /**
   * Build a direct URL for the invoice PDF endpoint (for window.open / <a href>).
   *
   * window.open() can't send custom headers, so the auth token is appended as a
   * `_token` query parameter — BaseApiPresenter accepts it as a fallback to the
   * standard Authorization header.
   */
  getInvoicePdfUrl(invoiceId) {
    const token = this.getToken() || '';
    const params = token ? `?_token=${encodeURIComponent(token)}` : '';
    return `${API_BASE}/invoices/${invoiceId}/pdf${params}`;
  }

  /**
   * URL pro PDF „Potvrzení objednávky inzerce" (window.open → token v ?_token=).
   */
  getOrderPdfUrl(orderId) {
    const token = this.getToken() || '';
    const params = token ? `?_token=${encodeURIComponent(token)}` : '';
    return `${API_BASE}/orders/${orderId}/pdf${params}`;
  }

  // Invoice Payments
  createInvoicePayment(invoiceId, data) {
    return this.post(`/invoices/${invoiceId}/payments/create`, data);
  }

  updateInvoicePayment(paymentId, data) {
    return this.put(`/invoices/payments/${paymentId}/update`, data);
  }

  deleteInvoicePayment(paymentId) {
    return this.delete(`/invoices/payments/${paymentId}/delete`);
  }

  // ==========================================
  // PÁROVÁNÍ BANKOVNÍCH PLATEB
  // ==========================================

  getBankPayments(params = {}) {
    return this.get('/bank-payments', params);
  }

  getBankPaymentStats() {
    return this.get('/bank-payments/stats');
  }

  getBankPayment(id) {
    return this.get(`/bank-payments/${id}`);
  }

  getBankModules() {
    return this.get('/bank-payments/banks');
  }

  getBankPaymentAudit(params = {}) {
    return this.get('/bank-payments/audit', params);
  }

  getBankSyncLog(limit = 30) {
    return this.get('/bank-payments/sync-log', { limit });
  }

  /** Kandidátní faktury pro ruční párování — číslo faktury, zákazník, číslo objednávky. */
  searchBankPaymentInvoices(q, includePaid = false) {
    return this.get('/bank-payments/invoice-search', { q, includePaid: includePaid ? 1 : 0 });
  }

  matchBankPayment(id, data) {
    return this.post(`/bank-payments/${id}/match`, data);
  }

  unmatchBankPayment(matchId) {
    return this.post(`/bank-payments/matches/${matchId}/unmatch`, {});
  }

  ignoreBankPayment(id) {
    return this.post(`/bank-payments/${id}/ignore`, {});
  }

  unignoreBankPayment(id) {
    return this.post(`/bank-payments/${id}/unignore`, {});
  }

  /**
   * Ruční spuštění „cronu" — endpoint je záměrně bez autorizace,
   * aby ho mohl volat i skutečný cron.
   */
  syncBankPayments(params = {}) {
    return this.get('/cron/bank-payments', { ...params, trigger: 'manual' });
  }

  // ==========================================
  // EXCHANGE RATES
  // ==========================================

  getLatestExchangeRates() {
    return this.get('/exchange-rates/latest');
  }

  /**
   * Kurz jedné měny ke konkrétnímu dni — podle něj se přepočítává faktura
   * v cizí měně (pro DPH se bere kurz ke dni uskutečnění plnění).
   * Odpověď nese `rate_date`: na nepracovní den vydává ČNB kurz posledního
   * pracovního dne, takže se počítá jiným dnem, než se ptáš.
   */
  getExchangeRateForDate(date, currency) {
    return this.get(`/exchange-rates/for-date?date=${encodeURIComponent(date)}&currency=${encodeURIComponent(currency)}`);
  }

  // ==========================================
  // USERS
  // ==========================================

  getUsers() {
    return this.get('/users');
  }

  getUser(id) {
    return this.get(`/users/${id}`);
  }

  createUser(data) {
    return this.post('/users/create', data);
  }

  updateUser(id, data) {
    return this.put(`/users/${id}/update`, data);
  }

  // ==========================================
  // NASTAVENÍ → SKUPINY (ACL)
  // ==========================================

  getGroups(search = '') {
    return this.get('/groups', { search });
  }

  getGroup(id) {
    return this.get(`/groups/${id}`);
  }

  /** Přehled „kdo je v jaké skupině". */
  getUsersWithGroups() {
    return this.get('/groups/users');
  }

  createGroup(data) {
    return this.post('/groups/create', data);
  }

  updateGroup(id, data) {
    return this.put(`/groups/${id}/update`, data);
  }

  setGroupUsers(id, userIds) {
    return this.put(`/groups/${id}/users`, { user_ids: userIds });
  }

  /** Katalog oprávnění — zdroj pravdy pro strom v Nastavení. */
  getRoles() {
    return this.get('/roles');
  }

  setGroupRoles(id, roleCodes) {
    return this.put(`/groups/${id}/roles`, { role_codes: roleCodes });
  }

  deleteGroup(id) {
    return this.delete(`/groups/${id}/delete`);
  }

  // ==========================================
  // PROVIZE / STATISTIKY — číselníky a nastavení
  // ==========================================

  /**
   * Všech sedm číselníků najednou.
   * `date` vrátí sazby platné k datu (uzavřená období se počítají původními).
   */
  getStatisticsSettings(date = '') {
    return this.get('/statistics/settings', date ? { date } : {});
  }

  /**
   * Uloží sazby s platností od data. Když sada s tím datem ještě není,
   * backend naklonuje současnou a změny zapíše do nové verze.
   */
  saveStatisticsRates(payload) {
    return this.put('/statistics/settings/rates', payload);
  }

  /** Období, za která existují uhrazené faktury. */
  getStatisticsPeriods() {
    return this.get('/statistics/periods');
  }

  /** Položková sestava provizí za období (YYYY-MM) — počítá backend. */
  getStatisticsCommissions(period) {
    return this.get('/statistics/commissions', { period });
  }

  /**
   * Sestava provizí do Excelu. `salespersonId` prázdné = celé období,
   * `filename` je název bez přípony (stejný tvar jako u tiskové sestavy).
   */
  exportStatisticsToXlsx(period, salespersonId = null, filename = null) {
    return this.postForFile('/statistics/export-xlsx', { period, salespersonId, filename }, {
      fallbackName: 'provize.xlsx',
    });
  }

  // --- ruční zásahy a uzávěrka (migrace 049) -------------------------------

  /** Přepis řádku sestavy. `values` nese desetinné sazby (0.17 = 17 %). */
  saveCommissionOverride(invoiceId, values, reason = null) {
    return this.put('/statistics/override', { invoiceId, values, reason });
  }

  /** Zruší přepis — řádek se vrátí k automatickému dopočtu. */
  deleteCommissionOverride(invoiceId) {
    return this.delete(`/statistics/override/${invoiceId}/delete`);
  }

  saveCommissionManualItem(data, id = null) {
    return id
      ? this.put(`/statistics/manual-item/${id}`, data)
      : this.post('/statistics/manual-item', data);
  }

  deleteCommissionManualItem(id) {
    return this.delete(`/statistics/manual-item/${id}/delete`);
  }

  /**
   * Vydání titulu pro úpravu plánů. Okno `from`/`to` je `YYYY-MM-DD`;
   * bez nich se vrátí celá historie (strop 500 řádků).
   */
  getStatisticsTitleIssues(titleId, { from = null, to = null } = {}) {
    return this.get(`/statistics/titles/${titleId}/issues`, { from, to });
  }

  /** Oprava plánu vydání — mění pásmo celého vydání, ne jen jednoho řádku. */
  saveCommissionPlanOverride(data) {
    return this.put('/statistics/plan-override', data);
  }

  closeCommissionPeriod(period, note = null) {
    return this.post(`/statistics/periods/${period}/close`, { note });
  }

  reopenCommissionPeriod(period) {
    return this.post(`/statistics/periods/${period}/reopen`);
  }

  getCommissionPeriodHistory(period) {
    return this.get(`/statistics/periods/${period}/history`);
  }

  /** Plnění plánů vydání za období. */
  getStatisticsTitlesPerformance(period) {
    return this.get('/statistics/titles-performance', { period });
  }

  /** Superprovize manažerů a odměny za tituly v gesci. */
  getStatisticsManagersRewards(period) {
    return this.get('/statistics/managers-rewards', { period });
  }

  /** Média a jejich zařazení pod provizní titul. */
  getStatisticsMedia(params = {}) {
    return this.get('/statistics/media', params);
  }

  /** Zařadí médium pod titul; `titleId: null` ho vyřadí. */
  assignStatisticsMedium(mediumId, payload) {
    return this.put(`/statistics/media/${mediumId}`, payload);
  }

  /** Klasifikace titulu + volitelně plán a slevy s platností od data. */
  saveStatisticsTitle(id, payload) {
    return this.put(`/statistics/titles/${id}`, payload);
  }

  /** Individuální úpravy bonusu obchodníka. */
  saveStatisticsSalesperson(id, payload) {
    return this.put(`/statistics/salespeople/${id}`, payload);
  }

  /** Role, superprovize a gesce manažera (gesce se přepisují celé). */
  saveStatisticsManager(id, payload) {
    return this.put(`/statistics/managers/${id}`, payload);
  }

  /** Udělá z obchodníka manažera (vypnutého jen zapne). */
  createStatisticsManager(payload) {
    return this.post('/statistics/managers/create', payload);
  }

  /** Mentoring: kdo koho zaučuje, za kolik a do kdy. Bez `id` zakládá nový. */
  saveStatisticsMentorship(id, payload) {
    return this.put(id ? `/statistics/mentorship/${id}` : '/statistics/mentorship', payload);
  }

  deleteStatisticsMentorship(id) {
    return this.delete(`/statistics/mentorship/${id}/delete`);
  }

  // ==========================================
  // MŮJ PROFIL (přihlášený uživatel)
  // ==========================================

  getProfile() {
    return this.get('/users/profile');
  }

  updateProfile(data) {
    return this.put('/users/profile/update', data);
  }

  changePassword(currentPassword, newPassword) {
    return this.post('/users/profile/password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  // ==========================================
  // NOTIFICATIONS
  // ==========================================

  getNotifications() {
    return this.get('/notifications');
  }

  markNotificationRead(id) {
    return this.put(`/notifications/${id}/read`);
  }

  // ==========================================
  // DASHBOARD
  // ==========================================

  getDashboardStats() {
    return this.get('/dashboard/stats');
  }

  // ==========================================
  // TAB STATE (persistence across sessions)
  // ==========================================

  loadTabState() {
    return this.get('/tabs/load');
  }

  saveTabState(tabs, activeTab) {
    return this.put('/tabs/save', { tabs, activeTab });
  }

  // ==========================================
  // NASTAVENÍ TABULEK (šířky sloupců per uživatel)
  // ==========================================

  /** Šířky sloupců v procentech viditelné šířky tabulky, nebo null. */
  getGridState(key) {
    return this.get('/grid-state', { key });
  }

  saveGridState(key, columns) {
    return this.put('/grid-state/save', { key, columns });
  }

  /** Smaže uložené nastavení → tabulka se příště změří znovu. */
  resetGridState(key) {
    return this.delete(`/grid-state/reset?key=${encodeURIComponent(key)}`);
  }

  // ==========================================
  // EDITIONS (Špíglování)
  // ==========================================

  lookupEdition({ mediumId, projectId, mutation }) {
    return this.get('/editions/lookup', {
      medium_id: mediumId,
      project_id: projectId,
      mutation: mutation ?? '',
    });
  }

  getEditionVersions(editionId) {
    return this.get(`/editions/${editionId}/versions`);
  }

  duplicateEditionVersion(editionId, name) {
    return this.post(`/editions/${editionId}/duplicate`, { name });
  }

  cloneEditionFromProject(editionId, sourceProjectId, name) {
    return this.post(`/editions/${editionId}/clone-from-project`, {
      source_project_id: sourceProjectId,
      name,
    });
  }

  /**
   * URL for the špíglování PDF — opened directly in a new window, so the
   * auth token rides in the query string.
   */
  getEditionPdfUrl(editionId, versionId) {
    const token = this.getToken() || '';
    const parts = [];
    if (versionId) parts.push(`version_id=${encodeURIComponent(versionId)}`);
    if (token) parts.push(`_token=${encodeURIComponent(token)}`);
    const qs = parts.length ? `?${parts.join('&')}` : '';
    return `${API_BASE}/editions/${editionId}/pdf${qs}`;
  }

  publishEditionVersion(versionId) {
    return this.post(`/edition-versions/${versionId}/publish`);
  }

  deleteEditionVersion(versionId) {
    return this.delete(`/edition-versions/${versionId}/delete`);
  }

  getEditionPages(versionId) {
    return this.get(`/edition-versions/${versionId}/pages`);
  }

  getEditionUnassigned(versionId) {
    return this.get(`/edition-versions/${versionId}/unassigned`);
  }

  createEditionPage(versionId, data) {
    return this.post(`/edition-versions/${versionId}/pages/create`, data);
  }

  updateEditionPage(pageId, data) {
    return this.put(`/edition-pages/${pageId}/update`, data);
  }

  deleteEditionPage(pageId) {
    return this.delete(`/edition-pages/${pageId}/delete`);
  }

  moveEditionPage(pageId, newPosition) {
    return this.post(`/edition-pages/${pageId}/move`, { new_position: newPosition });
  }

  createEditionPosition(pageId, data) {
    return this.post(`/edition-pages/${pageId}/positions/create`, data);
  }

  updateEditionPosition(positionId, data) {
    return this.put(`/edition-positions/${positionId}/update`, data);
  }

  deleteEditionPosition(positionId) {
    return this.delete(`/edition-positions/${positionId}/delete`);
  }

  // ==========================================
  // GLOBAL SEARCH
  // ==========================================

  globalSearch(query, limit = 10) {
    return this.get('/search', { q: query, limit });
  }

  // ==========================================
  // IMPORT
  // ==========================================

  importCrm() {
    return this.post('/import/crm');
  }

  importMedia() {
    return this.post('/import/media');
  }

  importPricelist() {
    return this.post('/import/pricelist');
  }
}

// Singleton instance
const api = new ApiClient();
export default api;
